import {
	PRESET_LIBRARY_KINDS,
	PRESET_LIBRARY_SYNC_CHANNEL,
	type PresetLibraryKind,
} from "@klarify/constants";

interface PresetLibraryChangeMessage {
	kind: PresetLibraryKind;
}

// One channel per window. postMessage never delivers to the channel object
// it was called on, so announcing and subscribing through the same object
// keeps a tab from invalidating twice for its own save.
let sharedChannel: BroadcastChannel | null | undefined;

function channel(): BroadcastChannel | null {
	if (sharedChannel === undefined) {
		sharedChannel =
			typeof BroadcastChannel === "undefined"
				? null
				: new BroadcastChannel(PRESET_LIBRARY_SYNC_CHANNEL);
	}
	return sharedChannel;
}

function isPresetLibraryKind(value: unknown): value is PresetLibraryKind {
	return (PRESET_LIBRARY_KINDS as readonly unknown[]).includes(value);
}

/** Tell the other tabs that a graph or table library changed. */
export function announcePresetLibraryChange(kind: PresetLibraryKind): void {
	const message: PresetLibraryChangeMessage = { kind };
	channel()?.postMessage(message);
}

/**
 * Run `listener` when ANOTHER tab announces a library change. Returns the
 * unsubscribe. The announcing tab is not notified (shared channel object),
 * so it invalidates its own cache directly.
 */
export function subscribePresetLibraryChanges(
	listener: (kind: PresetLibraryKind) => void,
): () => void {
	const target = channel();
	if (!target) {
		return () => {};
	}
	const onMessage = (event: MessageEvent<unknown>) => {
		const kind = (event.data as Partial<PresetLibraryChangeMessage> | null)
			?.kind;
		if (isPresetLibraryKind(kind)) {
			listener(kind);
		}
	};
	target.addEventListener("message", onMessage);
	return () => target.removeEventListener("message", onMessage);
}
